import {FreeDeliverySection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/basket_mov.types';
import {AddItemResponseStatus_Success_Section} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/add_item_status.types';
import {useTranslation} from '@aboutyou/i18n';
import {TrackContentPageView, useTrackFeature} from '@aboutyou/tracking';
import {
  BlurredOverlay,
  COLORS,
  Drawer,
  GenericIconName,
  Icon,
  Text,
  Z_INDEXES,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useEffect} from 'react';
import {MiniAdpDialogOpenedStateKey} from '../../../features/MiniADPDialog/useMiniAdpDialogOpenedState';
import {useBasketFlyoutOpenedState} from '../hooks/useBasketFlyoutOpenedState';
import {BasketFlyoutContent} from './BasketFlyoutContent';

export const BasketFlyout: React.FC<{
  freeDeliverySection?: FreeDeliverySection;
  statusSuccessSections: AddItemResponseStatus_Success_Section[];
  basketFlyoutStateManagerKey?: string;
  miniADPModalStateManagerKey?: MiniAdpDialogOpenedStateKey;
}> = ({
  freeDeliverySection,
  statusSuccessSections,
  basketFlyoutStateManagerKey,
  miniADPModalStateManagerKey,
}) => {
  const {t} = useTranslation();
  const trackFeature = useTrackFeature();
  const {
    close: closeBasketFlyout,
    isOpen: isBasketFlyoutOpen,
    productId,
  } = useBasketFlyoutOpenedState(basketFlyoutStateManagerKey);

  useEffect(() => {
    if (isBasketFlyoutOpen) {
      trackFeature({eventType: 'impression', label: 'basketFlyout'});
    }
  }, [isBasketFlyoutOpen]);

  return (
    <>
      {isBasketFlyoutOpen && (
        <TrackContentPageView
          key="basketFlyoutTracking"
          pageTitle="basket_flyout"
          overridePageProps={{type: 'basket_flyout'}}
        />
      )}

      <StyledDrawer
        renderInPortal
        open={isBasketFlyoutOpen}
        slideDirection="left"
        data-testid="basketFlyout"
        onClose={() => {
          trackFeature({eventType: 'close', label: 'basketFlyout'});
          closeBasketFlyout();
        }}
        OverlayComponent={StyledFlyoutOverlay}
        headerContent={
          <TitleContainer>
            <SuccessIcon asset={GenericIconName.AlertSuccessFill} size="S" />
            <Text size="px16" weight="bold">
              {t('Successfully added to your basket!')}
            </Text>
          </TitleContainer>
        }
        bodyContent={[
          <React.Fragment key="basketFlyoutActiveContent">
            <BasketFlyoutContent
              productId={productId}
              freeDeliverySection={freeDeliverySection}
              statusSuccessSections={statusSuccessSections}
              miniADPModalStateManagerKey={miniADPModalStateManagerKey}
              basketFlyoutStateManagerKey={basketFlyoutStateManagerKey}
            />
          </React.Fragment>,
        ]}
      />
    </>
  );
};

const StyledDrawer = styled(Drawer)`
  &&& {
    z-index: ${Z_INDEXES.TADARIDA_BASKET_FLYOUT};
    background-color: ${COLORS.White};
  }
`;

const StyledFlyoutOverlay = styled(BlurredOverlay)`
  z-index: ${Z_INDEXES.TADARIDA_BASKET_FLYOUT};
`;

const SuccessIcon = styled(Icon)`
  && {
    fill: ${COLORS.Green};
    margin-right: 10px;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
`;
